import React, { useEffect, useState, useRef } from "react";
import "mathlive";
import { convertLatexToMarkup } from "mathlive";
import "mathlive/static.css";
import "mathlive/fonts.css";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  ArrowLeft, Plus, Trash2, ChevronDown, ChevronRight, Check,
  Loader, Eye, Printer, Save, X, Pencil, BookOpen,
} from "lucide-react";
import {
  lmsGetManualPaperById, lmsUpdateManualPaper,
  lmsCreateSection, lmsUpdateSection, lmsDeleteSection,
  lmsCreateQuestion, lmsUpdateQuestion, lmsDeleteQuestion,
  lmsUpdateOption,
} from "../api/lmsApi";
import "../lms.css";

const Q_TYPES = [
  { value: "mcq",            label: "Multiple Choice (MCQ)" },
  { value: "true_false",     label: "True / False" },
  { value: "fill_blank",     label: "Fill in the Blank" },
  { value: "short_answer",   label: "Short Answer" },
  { value: "long_answer",    label: "Long Answer" },
  { value: "match_following",label: "Match the Following" },
];
const Q_TYPE_LABELS = Object.fromEntries(Q_TYPES.map((t) => [t.value, t.label]));

const SECTION_LABELS = ["A","B","C","D","E","F","G","H"];

/* ── tiny styled input ── */
const inp = (extra = {}) => ({
  width: "100%", padding: "8px 10px", borderRadius: 7, fontSize: 13,
  border: "1.5px solid #e5e7eb", outline: "none", color: "#111827",
  background: "#fff", boxSizing: "border-box", ...extra,
});

/* ── ConfirmModal ── */
function ConfirmModal({ message, onConfirm, onCancel }) {
  return (
    <div className="ms2-modal-overlay" onClick={onCancel}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 14, width: 380, maxWidth: "92vw",
        boxShadow: "0 20px 60px rgba(0,0,0,0.18)", overflow: "hidden",
      }}>
        <div style={{ height: 4, background: "linear-gradient(90deg,#dc2626,#ef4444)" }} />
        <div style={{ padding: "28px 24px 20px", textAlign: "center" }}>
          <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#fef2f2",
            border: "2px solid #fecaca", display: "flex", alignItems: "center",
            justifyContent: "center", margin: "0 auto 14px" }}>
            <Trash2 size={22} color="#dc2626" />
          </div>
          <div style={{ fontSize: 14, color: "#374151", lineHeight: 1.7 }}>{message}</div>
        </div>
        <div style={{ display: "flex", gap: 10, padding: "0 24px 20px" }}>
          <button onClick={onCancel} style={{
            flex: 1, padding: "9px 0", borderRadius: 8, fontSize: 13, fontWeight: 600,
            border: "1.5px solid #d1d5db", background: "#fff", color: "#374151", cursor: "pointer",
          }}>Cancel</button>
          <button onClick={onConfirm} style={{
            flex: 1, padding: "9px 0", borderRadius: 8, fontSize: 13, fontWeight: 700,
            border: "none", background: "#dc2626", color: "#fff", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
          }}><Trash2 size={13} /> Delete</button>
        </div>
      </div>
    </div>
  );
}

/* ── Match the Following — helpers ── */
const EMPTY_PAIRS = () => [{ a: "", b: "" }, { a: "", b: "" }, { a: "", b: "" }];

function parseMatchPairs(text) {
  if (!text) return EMPTY_PAIRS();
  try {
    const parsed = JSON.parse(text);
    if (parsed.pairs && parsed.pairs.length) return parsed.pairs;
  } catch {}
  // Legacy "A: x, y B: a, b" format
  const m = text.match(/^(.*?)\s*B\s*:\s*(.+)$/is);
  if (!m) return [{ a: text, b: "" }];
  const colA = m[1].replace(/^A\s*:\s*/i, "").trim().split(/[,،;]\s*/).filter(Boolean);
  const colB = m[2].trim().split(/[,،;]\s*/).filter(Boolean);
  const len = Math.max(colA.length, colB.length, 1);
  return Array.from({ length: len }, (_, i) => ({ a: colA[i] || "", b: colB[i] || "" }));
}

function serializeMatchPairs(pairs) {
  return JSON.stringify({ pairs });
}

/* ── MathLive cell (question / answer input) ── */
function MathCell({ value, onChange, placeholder }) {
  const ref = useRef();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.value = value || "";
    el.setAttribute("placeholder", placeholder || "");
    const handler = () => onChange(el.value);
    el.addEventListener("input", handler);
    return () => el.removeEventListener("input", handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <math-field
      ref={ref}
      default-mode="text"
      virtual-keyboard-mode="onfocus"
      style={{
        width: "100%", display: "block",
        border: "1.5px solid #c7d2fe", borderRadius: 8,
        padding: "6px 10px", fontSize: 14, "--hue": "250",
        background: "#fafbff",
      }}
    />
  );
}

/* ── MathLive cell (options — saves on blur) ── */
function MathCellBlur({ value, onChange, onBlur, placeholder }) {
  const ref = useRef();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.value = value || "";
    el.setAttribute("placeholder", placeholder || "");
    const onInput = () => onChange(el.value);
    const onBlurEvt = () => onBlur && onBlur();
    el.addEventListener("input", onInput);
    el.addEventListener("blur", onBlurEvt);
    return () => { el.removeEventListener("input", onInput); el.removeEventListener("blur", onBlurEvt); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <math-field
      ref={ref}
      default-mode="text"
      virtual-keyboard-mode="onfocus"
      style={{
        flex: 1, display: "block", border: "none",
        padding: "2px 4px", fontSize: 14, "--hue": "250",
        background: "transparent",
      }}
    />
  );
}

/* ── MathPreview — renders LaTeX or plain text ── */
function MathPreview({ latex }) {
  if (!latex) return null;
  // Only run convertLatexToMarkup when actual LaTeX commands are present
  const hasLatex = /[\\^_{}]/.test(latex);
  if (!hasLatex) return <span style={{ fontFamily: "inherit" }}>{latex}</span>;
  try {
    const html = convertLatexToMarkup(latex, { mathstyle: "textstyle" });
    return <span dangerouslySetInnerHTML={{ __html: html }} />;
  } catch {
    return <span>{latex}</span>;
  }
}

/* ── Match the Following Editor ── */
function MatchEditor({ value, onChange }) {
  const [pairs, setPairs] = useState(() => parseMatchPairs(value));

  const notify = (p) => onChange(serializeMatchPairs(p));

  const updateA = (i, v) => {
    const next = pairs.map((p, idx) => idx === i ? { ...p, a: v } : p);
    setPairs(next); notify(next);
  };
  const updateB = (i, v) => {
    const next = pairs.map((p, idx) => idx === i ? { ...p, b: v } : p);
    setPairs(next); notify(next);
  };
  const addRow = () => {
    const next = [...pairs, { a: "", b: "" }];
    setPairs(next); notify(next);
  };
  const removeRow = (i) => {
    if (pairs.length === 1) return;
    const next = pairs.filter((_, idx) => idx !== i);
    setPairs(next); notify(next);
  };

  const thStyle = {
    fontSize: 11, fontWeight: 700, color: "#6b7280",
    padding: "6px 8px", textAlign: "left", borderBottom: "1.5px solid #e5e7eb",
  };

  return (
    <div style={{ marginTop: 10 }}>
      {/* Header hint */}
      <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 6 }}>
        Click any cell to open the math keyboard. Type text or use the virtual keyboard for equations and symbols.
      </div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={{ ...thStyle, width: 32 }}>#</th>
            <th style={thStyle}>Column A</th>
            <th style={{ ...thStyle, width: 20, textAlign: "center" }}></th>
            <th style={thStyle}>Column B</th>
            <th style={{ ...thStyle, width: 30 }}></th>
          </tr>
        </thead>
        <tbody>
          {pairs.map((pair, i) => (
            <tr key={i}>
              <td style={{ padding: "6px 6px", fontSize: 12, color: "#9ca3af", verticalAlign: "middle" }}>
                {i + 1}.
              </td>
              <td style={{ padding: "6px 4px", verticalAlign: "top" }}>
                <MathCell
                  value={pair.a}
                  onChange={(v) => updateA(i, v)}
                  placeholder={`Item ${i + 1}`}
                />
              </td>
              <td style={{ padding: "6px 4px", textAlign: "center", color: "#9ca3af",
                fontSize: 16, verticalAlign: "middle" }}>→</td>
              <td style={{ padding: "6px 4px", verticalAlign: "top" }}>
                <MathCell
                  value={pair.b}
                  onChange={(v) => updateB(i, v)}
                  placeholder={`Match ${i + 1}`}
                />
              </td>
              <td style={{ padding: "6px 4px", textAlign: "center", verticalAlign: "middle" }}>
                <button onClick={() => removeRow(i)} disabled={pairs.length === 1} style={{
                  width: 26, height: 26, borderRadius: 6, border: "1px solid #fecaca",
                  background: pairs.length === 1 ? "#f9fafb" : "#fff5f5",
                  color: pairs.length === 1 ? "#d1d5db" : "#dc2626",
                  cursor: pairs.length === 1 ? "default" : "pointer",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <X size={12} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <button onClick={addRow} style={{
        marginTop: 10, padding: "5px 14px", borderRadius: 7, fontSize: 12, fontWeight: 600,
        border: "1.5px dashed #a5b4fc", background: "#f5f3ff", color: "#4f46e5", cursor: "pointer",
        display: "flex", alignItems: "center", gap: 5,
      }}>
        <Plus size={12} /> Add Row
      </button>
    </div>
  );
}

/* ── MCQ Options Editor ── */
const OPT_COLORS = { A: "#4f46e5", B: "#0891b2", C: "#d97706", D: "#16a34a", E: "#dc2626", True: "#16a34a", False: "#dc2626" };

function McqOptions({ options, onOptionSaved }) {
  const [saving, setSaving] = useState({});
  const latexRef = useRef({});
  options.forEach((o) => { if (latexRef.current[o.id] === undefined) latexRef.current[o.id] = o.option_text || ""; });

  const saveOption = async (opt) => {
    const text = latexRef.current[opt.id] ?? "";
    if (text === (opt.option_text || "")) return;
    setSaving((p) => ({ ...p, [opt.id]: true }));
    try { await lmsUpdateOption(opt.id, { option_text: text }); onOptionSaved(); }
    catch { toast.error("Failed to save option"); }
    finally { setSaving((p) => ({ ...p, [opt.id]: false })); }
  };

  const markCorrect = async (opt) => {
    setSaving((p) => ({ ...p, [opt.id]: true }));
    try { await lmsUpdateOption(opt.id, { is_correct: 1 }); onOptionSaved(); }
    catch { toast.error("Failed to update"); }
    finally { setSaving((p) => ({ ...p, [opt.id]: false })); }
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 10 }}>
      {options.map((opt) => {
        const color = OPT_COLORS[opt.option_label] || "#6b7280";
        return (
          <div key={opt.id} style={{
            border: `2px solid ${opt.is_correct ? "#16a34a" : "#e5e7eb"}`,
            borderRadius: 10, background: opt.is_correct ? "#f0fdf4" : "#fff",
            overflow: "hidden", position: "relative",
          }}>
            {/* Label badge */}
            <div style={{
              position: "absolute", top: 8, left: 8, width: 24, height: 24,
              borderRadius: 6, background: color, color: "#fff",
              fontSize: 11, fontWeight: 800,
              display: "flex", alignItems: "center", justifyContent: "center",
              zIndex: 1, pointerEvents: "none",
            }}>{opt.option_label}</div>

            <MathCellBlur
              value={opt.option_text || ""}
              onChange={(v) => { latexRef.current[opt.id] = v; }}
              onBlur={() => saveOption(opt)}
              placeholder={`Option ${opt.option_label}`}
            />

            {/* Footer: saving / mark correct */}
            <div style={{
              borderTop: `1px solid ${opt.is_correct ? "#bbf7d0" : "#f1f5f9"}`,
              padding: "4px 8px", display: "flex", alignItems: "center",
              justifyContent: "flex-end", gap: 6,
            }}>
              {saving[opt.id] ? <Loader size={11} className="ms2-spin" color="#6b7280" /> : (
                opt.is_correct
                  ? <span style={{ fontSize: 11, fontWeight: 700, color: "#16a34a" }}>✓ Correct Answer</span>
                  : <button onClick={() => markCorrect(opt)} style={{
                      fontSize: 11, fontWeight: 700, padding: "2px 8px", borderRadius: 5,
                      border: "1.5px solid #bbf7d0", background: "#f0fdf4",
                      color: "#16a34a", cursor: "pointer",
                    }}>Mark Correct</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── Section type color map ── */
const SEC_COLORS = {
  mcq: "#4f46e5", true_false: "#0891b2", fill_blank: "#d97706",
  short_answer: "#16a34a", long_answer: "#7c3aed", match_following: "#dc2626",
};

/* ── Question Card ── */
function QuestionCard({ question, index, sectionType, onDelete, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [text, setText]       = useState(question.question_text);
  const [marks, setMarks]     = useState(question.marks ?? "");
  const [answer, setAnswer]   = useState(question.correct_answer || "");
  const [saving, setSaving]   = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await lmsUpdateQuestion(question.id, {
        question_text: text.trim(),
        marks: marks || null,
        correct_answer: answer || null,
      });
      toast.success("Saved");
      setEditing(false);
      onRefresh();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const hasMcqOptions = sectionType === "mcq" || sectionType === "true_false";
  const color = SEC_COLORS[sectionType] || "#4f46e5";

  return (
    <div style={{
      background: "#fff", borderRadius: 10, marginBottom: 10,
      border: "1px solid #e5e7eb",
      boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
    }}>
      {/* Row: number + question text + actions */}
      <div style={{ display: "flex", gap: 0, alignItems: "flex-start" }}>
        {/* Number badge */}
        <div style={{
          width: 36, flexShrink: 0, padding: "14px 0",
          display: "flex", justifyContent: "center", alignItems: "flex-start",
          borderRight: `3px solid ${color}`,
        }}>
          <span style={{
            width: 24, height: 24, borderRadius: 6, background: color,
            color: "#fff", fontSize: 12, fontWeight: 800,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>{index}</span>
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0, padding: "12px 14px" }}>
          {/* Question text */}
          {editing ? (
            sectionType === "match_following"
              ? <MatchEditor value={text} onChange={setText} />
              : <MathCell value={text} onChange={setText} placeholder="Type question..." />
          ) : (
            sectionType === "match_following" ? (
              <div style={{ fontSize: 13, color: "#374151", lineHeight: 1.7 }}>
                {parseMatchPairs(question.question_text).map((p, i) => (
                  <span key={i} style={{
                    display: "inline-flex", alignItems: "center", gap: 5,
                    background: "#f0f4ff", border: "1px solid #c7d2fe", borderRadius: 6,
                    padding: "2px 8px", fontSize: 12, marginRight: 6, marginBottom: 4,
                  }}>
                    <MathPreview latex={p.a || "—"} />
                    <span style={{ color: "#9ca3af" }}>→</span>
                    <MathPreview latex={p.b || "—"} />
                  </span>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 14, color: "#111827", lineHeight: 1.7, fontWeight: 500 }}>
                <MathPreview latex={question.question_text} />
              </div>
            )
          )}

          {/* Edit fields: marks + answer */}
          {editing && (
            <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
              <div>
                <label style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, display: "block", marginBottom: 2 }}>Marks</label>
                <input type="number" value={marks} onChange={(e) => setMarks(e.target.value)}
                  placeholder="1" style={inp({ width: 80 })} />
              </div>
              {(sectionType === "fill_blank" || sectionType === "short_answer") && (
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, display: "block", marginBottom: 2 }}>Answer</label>
                  <MathCellBlur value={answer} onChange={setAnswer} placeholder="Correct answer..." />
                </div>
              )}
            </div>
          )}

          {/* Save / Cancel */}
          {editing && (
            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              <button onClick={handleSave} disabled={saving} style={{
                padding: "6px 16px", borderRadius: 7, fontSize: 13, fontWeight: 700,
                border: "none", background: color, color: "#fff", cursor: "pointer",
                display: "flex", alignItems: "center", gap: 5,
              }}>
                {saving ? <Loader size={12} className="ms2-spin" /> : <Save size={12} />} Save
              </button>
              <button onClick={() => { setText(question.question_text); setEditing(false); }} style={{
                padding: "6px 12px", borderRadius: 7, fontSize: 13, fontWeight: 600,
                border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280", cursor: "pointer",
              }}>Cancel</button>
            </div>
          )}

          {/* MCQ / True-False options */}
          {!editing && hasMcqOptions && question.options?.length > 0 && (
            <McqOptions options={question.options} onOptionSaved={onRefresh} />
          )}

          {/* Meta footer */}
          {!editing && (
            <div style={{ marginTop: 8, fontSize: 11, color: "#9ca3af", display: "flex", gap: 10 }}>
              <span>{question.marks ?? "—"} mark</span>
              {question.correct_answer && <span>· Answer: <MathPreview latex={question.correct_answer} /></span>}
            </div>
          )}
        </div>

        {/* Edit / Delete actions */}
        {!editing && (
          <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "10px 10px 10px 0", flexShrink: 0 }}>
            <button onClick={() => setEditing(true)} title="Edit" style={{
              width: 30, height: 30, borderRadius: 7, border: "1px solid #e0e7ff",
              background: "#f0f4ff", color: color, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}><Pencil size={12} /></button>
            <button onClick={onDelete} title="Delete" style={{
              width: 30, height: 30, borderRadius: 7, border: "1px solid #fee2e2",
              background: "#fff5f5", color: "#dc2626", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
            }}><Trash2 size={12} /></button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Section Card ── */
function SectionCard({ section, onRefresh, onDelete }) {
  const [open, setOpen]     = useState(true);
  const [addingQ, setAddingQ] = useState(false);
  const [newQText, setNewQText] = useState(
    section.question_type === "match_following" ? serializeMatchPairs(EMPTY_PAIRS()) : ""
  );
  const [savingQ, setSavingQ]   = useState(false);
  const [confirmQ, setConfirmQ] = useState(null);

  const handleAddQuestion = async () => {
    const isMatch = section.question_type === "match_following";
    const hasContent = isMatch
      ? (() => { try { const p = JSON.parse(newQText); return p.pairs?.some((pr) => pr.a.trim()); } catch { return false; } })()
      : newQText.trim().length > 0;
    if (!hasContent) return toast.error(isMatch ? "Add at least one item in Column A" : "Question text is required");
    setSavingQ(true);
    try {
      await lmsCreateQuestion(section.id, { question_text: newQText.trim() });
      setNewQText(section.question_type === "match_following" ? serializeMatchPairs(EMPTY_PAIRS()) : "");
      setAddingQ(false);
      onRefresh();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add question");
    } finally { setSavingQ(false); }
  };

  const handleDeleteQuestion = async () => {
    try {
      await lmsDeleteQuestion(confirmQ.id);
      toast.success("Question deleted");
      setConfirmQ(null);
      onRefresh();
    } catch { toast.error("Failed to delete"); }
  };

  const color    = SEC_COLORS[section.question_type] || "#4f46e5";
  const qCount   = section.questions?.length || 0;
  const secMarks = section.questions?.reduce((s, q) => s + parseFloat(q.marks || section.marks_per_q || 0), 0) || 0;
  const qTypeLabel = Q_TYPE_LABELS[section.question_type] || section.question_type;

  return (
    <div style={{
      borderRadius: 12, marginBottom: 16, overflow: "hidden",
      border: `1.5px solid ${color}22`,
      boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
    }}>
      {/* Section header */}
      <div style={{
        padding: "12px 16px",
        background: `linear-gradient(135deg, ${color}10, ${color}05)`,
        borderBottom: `1.5px solid ${color}22`,
        display: "flex", alignItems: "center", gap: 12,
      }}>
        {/* Color bar */}
        <div style={{ width: 4, height: 40, borderRadius: 3, background: color, flexShrink: 0 }} />

        {/* Label + info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <span style={{
              fontWeight: 800, fontSize: 15, color: "#111827",
              background: `${color}18`, borderRadius: 6, padding: "2px 10px",
              border: `1px solid ${color}33`,
            }}>
              Section {section.section_label}
            </span>
            {section.title && <span style={{ fontSize: 13, color: "#6b7280" }}>— {section.title}</span>}
            <span style={{
              fontSize: 11, fontWeight: 700, color, background: `${color}12`,
              border: `1px solid ${color}30`, borderRadius: 5, padding: "1px 7px",
            }}>{qTypeLabel}</span>
          </div>
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 5, display: "flex", gap: 12 }}>
            <span>{qCount} question{qCount !== 1 ? "s" : ""}</span>
            <span>{section.marks_per_q} mark each</span>
            <span style={{ fontWeight: 700, color }}>Total: {secMarks.toFixed(1)}</span>
          </div>
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
          <button onClick={() => setOpen((p) => !p)} style={{
            width: 32, height: 32, borderRadius: 8, border: "1px solid #e5e7eb",
            background: "#fff", color: "#6b7280", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
          </button>
          <button onClick={onDelete} style={{
            width: 32, height: 32, borderRadius: 8, border: "1px solid #fee2e2",
            background: "#fff5f5", color: "#dc2626", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}><Trash2 size={13} /></button>
        </div>
      </div>

      {/* Questions + add */}
      {open && (
        <div style={{ padding: "14px 16px", background: "#fff" }}>
          {qCount === 0 && !addingQ && (
            <div style={{
              textAlign: "center", padding: "24px 16px",
              background: "#fafafa", borderRadius: 8, border: "1.5px dashed #e5e7eb",
              color: "#9ca3af", fontSize: 13, marginBottom: 10,
            }}>
              No questions yet — click <strong>+ Add Question</strong> to start.
            </div>
          )}

          {section.questions?.map((q, qi) => (
            <QuestionCard
              key={q.id}
              question={q}
              index={qi + 1}
              sectionType={section.question_type}
              onRefresh={onRefresh}
              onDelete={() => setConfirmQ(q)}
            />
          ))}

          {/* Add question panel */}
          {addingQ ? (
            <div style={{
              border: `1.5px solid ${color}44`, borderRadius: 10,
              padding: "14px 16px", background: `${color}06`, marginTop: 6,
            }}>
              <div style={{ fontSize: 13, fontWeight: 700, color, marginBottom: 8 }}>
                {section.question_type === "match_following"
                  ? `Question ${qCount + 1} — Enter Match Pairs`
                  : `Question ${qCount + 1}`}
              </div>
              {section.question_type === "match_following" ? (
                <MatchEditor value={newQText} onChange={setNewQText} />
              ) : (
                <MathCell value={newQText} onChange={setNewQText} placeholder="Type question here..." />
              )}
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <button onClick={handleAddQuestion} disabled={savingQ} style={{
                  padding: "8px 20px", borderRadius: 8, fontSize: 13, fontWeight: 700,
                  border: "none", background: color, color: "#fff", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 6,
                }}>
                  {savingQ ? <Loader size={12} className="ms2-spin" /> : <Check size={13} />}
                  Save Question
                </button>
                <button onClick={() => { setAddingQ(false); setNewQText(section.question_type === "match_following" ? serializeMatchPairs(EMPTY_PAIRS()) : ""); }} style={{
                  padding: "8px 16px", borderRadius: 8, fontSize: 13, fontWeight: 600,
                  border: "1px solid #e5e7eb", background: "#fff", color: "#6b7280", cursor: "pointer",
                }}>Cancel</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setAddingQ(true)} style={{
              width: "100%", padding: "10px",
              borderRadius: 9, border: `1.5px dashed ${color}66`,
              background: `${color}08`, color, fontSize: 13, fontWeight: 700,
              cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 7,
              marginTop: qCount > 0 ? 6 : 0,
            }}>
              <Plus size={14} /> Add Question
            </button>
          )}
        </div>
      )}

      {confirmQ && (
        <ConfirmModal
          message={<>Delete this question? This cannot be undone.</>}
          onConfirm={handleDeleteQuestion}
          onCancel={() => setConfirmQ(null)}
        />
      )}
    </div>
  );
}

/* ── Add Section Modal ── */
function AddSectionModal({ nextLabel, onClose, onSave }) {
  const [form, setForm] = useState({
    section_label: nextLabel, title: "",
    question_type: "mcq", marks_per_q: "1", instructions: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } finally { setSaving(false); }
  };

  return (
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 14, width: 460, maxWidth: "92vw",
        boxShadow: "0 20px 60px rgba(0,0,0,0.15)", overflow: "hidden",
      }}>
        <div style={{ height: 4, background: "linear-gradient(90deg,#4f46e5,#7c3aed)" }} />
        <div style={{ padding: "18px 22px 14px", borderBottom: "1px solid #f1f5f9",
          display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#111827" }}>Add Section</div>
          <button onClick={onClose} style={{ border: "none", background: "transparent",
            cursor: "pointer", color: "#9ca3af" }}><X size={16} /></button>
        </div>

        <div style={{ padding: "16px 22px", display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "80px 1fr", gap: 10 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Label</label>
              <select style={inp({ marginTop: 4 })} value={form.section_label}
                onChange={(e) => set("section_label", e.target.value)}>
                {SECTION_LABELS.map((l) => <option key={l} value={l}>{l}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Section Title</label>
              <input style={inp({ marginTop: 4 })} value={form.title}
                onChange={(e) => set("title", e.target.value)}
                placeholder="Multiple Choice Questions" />
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 100px", gap: 10 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Question Type</label>
              <select style={inp({ marginTop: 4 })} value={form.question_type}
                onChange={(e) => set("question_type", e.target.value)}>
                {Q_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Marks/Q</label>
              <input type="number" style={inp({ marginTop: 4 })} value={form.marks_per_q}
                onChange={(e) => set("marks_per_q", e.target.value)} min="0.5" step="0.5" />
            </div>
          </div>

          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>Section Instructions</label>
            <textarea style={inp({ marginTop: 4, resize: "vertical" })} rows={2}
              value={form.instructions} onChange={(e) => set("instructions", e.target.value)}
              placeholder="e.g. Attempt all questions" />
          </div>
        </div>

        <div style={{ padding: "12px 22px 18px", display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={{
            padding: "8px 18px", borderRadius: 8, fontSize: 13, fontWeight: 600,
            border: "1.5px solid #e5e7eb", background: "#fff", color: "#374151", cursor: "pointer",
          }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{
            padding: "8px 20px", borderRadius: 8, fontSize: 13, fontWeight: 700,
            border: "none", background: "linear-gradient(135deg,#4f46e5,#7c3aed)",
            color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
          }}>
            {saving ? <Loader size={12} className="ms2-spin" /> : <Plus size={13} />}
            Add Section
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Print Preview Modal ── */
function PrintPreview({ paper, onClose }) {
  const ref = useRef();

  const handlePrint = () => {
    const win = window.open("", "_blank");
    win.document.write(`
      <html><head><title>${paper.title}</title>
      <link id="ml-css" rel="stylesheet" href="https://unpkg.com/mathlive/mathlive-static.css" />
      <link rel="stylesheet" href="https://unpkg.com/mathlive/mathlive-fonts.css" />
      <style>
        body { font-family: Arial, sans-serif; margin: 32px; color: #111; }
        h1 { font-size: 20px; text-align: center; margin: 0; }
        .meta { text-align: center; font-size: 13px; color: #444; margin: 6px 0 18px; }
        .divider { border-top: 2px solid #111; margin: 10px 0; }
        .instructions { font-size: 12px; background: #fff9db; padding: 8px 12px; border-left: 3px solid #fbbf24; margin-bottom: 16px; }
        .section-title { font-size: 14px; font-weight: bold; margin: 18px 0 6px;
          border-bottom: 1px solid #ddd; padding-bottom: 4px; }
        .section-meta { font-size: 11px; color: #666; margin-bottom: 10px; }
        .question { margin-bottom: 14px; }
        .q-text { font-size: 13px; margin-bottom: 6px; }
        .options { display: grid; grid-template-columns: 1fr 1fr; gap: 2px 20px; font-size: 12px; margin-left: 20px; }
        .blank { display: inline-block; width: 80px; border-bottom: 1px solid #111; }
        @media print { body { margin: 16px; } }
      </style></head><body>
      ${ref.current.innerHTML}
      </body></html>
    `);
    win.document.close();
    win.focus();
    win.onload = () => {
      const css = win.document.getElementById("ml-css");
      if (css && !css.sheet) {
        css.onload = () => win.print();
      } else {
        win.print();
      }
    };
  };

  const totalMarks = paper.sections?.reduce((s, sec) =>
    s + (sec.questions?.reduce((sq, q) => sq + parseFloat(q.marks || sec.marks_per_q || 0), 0) || 0), 0) || 0;

  return (
    <div className="ms2-modal-overlay" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        background: "#fff", borderRadius: 14, width: "90vw", maxWidth: 780,
        maxHeight: "92vh", display: "flex", flexDirection: "column",
        boxShadow: "0 24px 64px rgba(0,0,0,0.18)", overflow: "hidden",
      }}>
        {/* Modal header */}
        <div style={{ padding: "14px 20px", borderBottom: "1px solid #e5e7eb",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "#1e1b4b" }}>
          <div style={{ fontWeight: 700, fontSize: 15, color: "#fff" }}>Print Preview</div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handlePrint} style={{
              display: "flex", alignItems: "center", gap: 6, padding: "7px 16px",
              borderRadius: 8, border: "none", background: "#4f46e5", color: "#fff",
              fontSize: 13, fontWeight: 700, cursor: "pointer",
            }}>
              <Printer size={14} /> Print / Save PDF
            </button>
            <button onClick={onClose} style={{ border: "none", background: "transparent",
              cursor: "pointer", color: "#a5b4fc" }}><X size={18} /></button>
          </div>
        </div>

        {/* Paper preview */}
        <div style={{ flex: 1, overflowY: "auto", padding: "28px 32px", background: "#f8fafc" }}>
          <div ref={ref} style={{ background: "#fff", padding: "32px 36px",
            boxShadow: "0 2px 16px rgba(0,0,0,0.08)", borderRadius: 8, maxWidth: 680, margin: "0 auto" }}>

            {/* Header */}
            {paper.header_text && (
              <div style={{ textAlign: "center", fontSize: 13, color: "#555", marginBottom: 6 }}>
                {paper.header_text}
              </div>
            )}
            <h1 style={{ fontSize: 20, textAlign: "center", margin: "0 0 6px", fontWeight: 800 }}>
              {paper.title}
            </h1>
            <div style={{ textAlign: "center", fontSize: 13, color: "#555", marginBottom: 4 }}>
              {[paper.class_name, paper.subject_name].filter(Boolean).join(" · ")}
              {paper.paper_set ? ` | Set: ${paper.paper_set}` : ""}
              {paper.year ? ` | Year: ${paper.year}` : ""}
            </div>
            <div style={{ textAlign: "center", fontSize: 13, color: "#555", marginBottom: 10 }}>
              {paper.duration_minutes ? `Time: ${paper.duration_minutes} min` : ""}
              {paper.duration_minutes && totalMarks ? " | " : ""}
              {totalMarks ? `Total Marks: ${totalMarks}` : ""}
            </div>
            <div style={{ borderTop: "2px solid #111", borderBottom: "2px solid #111",
              margin: "10px 0", padding: "1px 0" }} />

            {/* Instructions */}
            {paper.instructions && (
              <div style={{ fontSize: 12, background: "#fffbeb", padding: "8px 12px",
                borderLeft: "3px solid #fbbf24", margin: "12px 0", lineHeight: 1.7 }}>
                <strong>Instructions:</strong> {paper.instructions}
              </div>
            )}

            {/* Sections */}
            {paper.sections?.map((sec, si) => (
              <div key={sec.id}>
                <div style={{ fontWeight: 800, fontSize: 14, marginTop: 20, marginBottom: 4 }}>
                  SECTION {sec.section_label}{sec.title ? ` — ${sec.title}` : ""}
                  <span style={{ float: "right", fontWeight: 600, fontSize: 13 }}>
                    [{sec.marks_per_q} mark{sec.marks_per_q !== 1 ? "s" : ""} each]
                  </span>
                </div>
                {sec.instructions && (
                  <div style={{ fontSize: 11, color: "#555", marginBottom: 8 }}>{sec.instructions}</div>
                )}
                <div style={{ borderBottom: "1px solid #ddd", marginBottom: 10 }} />

                {sec.questions?.map((q, qi) => {
                  // For fill_blank: replace ___ or _ sequences with inline blank
                  const renderFillBlank = (text) => {
                    const parts = text.split(/(_+)/g);
                    return parts.map((part, i) =>
                      /^_+$/.test(part)
                        ? <span key={i} style={{ display: "inline-block", minWidth: 80,
                            borderBottom: "1.5px solid #111", marginLeft: 2, marginRight: 2 }}>&nbsp;</span>
                        : part
                    );
                  };

                  // For match_following: render pairs from JSON or legacy format
                  const renderMatchFollowing = (text) => {
                    const pairs = parseMatchPairs(text);
                    return (
                      <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 8, fontSize: 12 }}>
                        <thead>
                          <tr>
                            <th style={{ width: "8%", padding: "4px 6px", border: "1px solid #ccc",
                              background: "#f3f4f6", fontWeight: 700, fontSize: 11 }}>No.</th>
                            <th style={{ width: "42%", padding: "4px 8px", border: "1px solid #ccc",
                              background: "#f3f4f6", fontWeight: 700, fontSize: 11 }}>Column A</th>
                            <th style={{ width: "8%", padding: "4px 6px", border: "1px solid #ccc",
                              background: "#f3f4f6", fontWeight: 700, fontSize: 11 }}>Match</th>
                            <th style={{ width: "42%", padding: "4px 8px", border: "1px solid #ccc",
                              background: "#f3f4f6", fontWeight: 700, fontSize: 11 }}>Column B</th>
                          </tr>
                        </thead>
                        <tbody>
                          {pairs.map((pair, i) => (
                            <tr key={i}>
                              <td style={{ padding: "5px 6px", border: "1px solid #ddd",
                                textAlign: "center", color: "#555" }}>{i + 1}.</td>
                              <td style={{ padding: "5px 8px", border: "1px solid #ddd" }}>
                                <MathPreview latex={pair.a || ""} />
                              </td>
                              <td style={{ padding: "5px 6px", border: "1px solid #ddd",
                                textAlign: "center" }}>
                                <span style={{ display: "inline-block", width: 36,
                                  borderBottom: "1px solid #111" }}>&nbsp;</span>
                              </td>
                              <td style={{ padding: "5px 8px", border: "1px solid #ddd" }}>
                                <MathPreview latex={pair.b || ""} />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );
                  };

                  return (
                  <div key={q.id} style={{ marginBottom: 18 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, lineHeight: 1.7 }}>
                      Q{q.question_no || qi + 1}.{" "}
                      {sec.question_type === "fill_blank"
                        ? renderFillBlank(q.question_text)
                        : <MathPreview latex={q.question_text} />}
                      {q.marks && parseFloat(q.marks) !== parseFloat(sec.marks_per_q) && (
                        <span style={{ float: "right", fontSize: 12, fontWeight: 400, color: "#555" }}>
                          [{q.marks} marks]
                        </span>
                      )}
                    </div>

                    {/* MCQ / True-False Options */}
                    {q.options?.length > 0 && sec.question_type !== "match_following" && (
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "4px 24px",
                        fontSize: 12, marginTop: 6, marginLeft: 20 }}>
                        {q.options.map((opt) => (
                          <div key={opt.id} style={{ display: "flex", gap: 4, alignItems: "center" }}>
                            <span>({opt.option_label})</span>
                            <MathPreview latex={opt.option_text || "___"} />
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Match the Following — two column table */}
                    {sec.question_type === "match_following" && (
                      <div style={{ marginLeft: 20, marginTop: 4 }}>
                        {renderMatchFollowing(q.question_text)}
                      </div>
                    )}

                    {/* Answer space for short/long answer */}
                    {sec.question_type === "short_answer" && (
                      <div style={{ marginTop: 16, marginBottom: 4 }}>
                        {[1,2].map((l) => (
                          <div key={l} style={{ borderBottom: "1px dotted #bbb", marginBottom: 14 }} />
                        ))}
                      </div>
                    )}
                    {sec.question_type === "long_answer" && (
                      <div style={{ marginTop: 16, marginBottom: 4 }}>
                        {[1,2,3,4].map((l) => (
                          <div key={l} style={{ borderBottom: "1px dotted #bbb", marginBottom: 14 }} />
                        ))}
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════
   MAIN — Paper Builder Page
══════════════════════════════════════════ */
export default function ManualPaperBuilder() {
  const { id }     = useParams();
  const navigate   = useNavigate();

  const [paper, setPaper]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [addSection, setAddSection] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [confirmSec, setConfirmSec] = useState(null);
  const [savingPub, setSavingPub] = useState(false);

  const loadPaper = async () => {
    try {
      const res = await lmsGetManualPaperById(id);
      setPaper(res.data);
    } catch (err) {
      toast.error("Failed to load paper");
    } finally { setLoading(false); }
  };

  useEffect(() => { loadPaper(); }, [id]);

  const handleAddSection = async (form) => {
    try {
      await lmsCreateSection(id, {
        section_label: form.section_label,
        title: form.title || null,
        question_type: form.question_type,
        marks_per_q: parseFloat(form.marks_per_q) || 1,
        instructions: form.instructions || null,
        sort_order: paper?.sections?.length || 0,
      });
      toast.success("Section added");
      loadPaper();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to add section");
    }
  };

  const handleDeleteSection = async () => {
    try {
      await lmsDeleteSection(confirmSec.id);
      toast.success("Section deleted");
      setConfirmSec(null);
      loadPaper();
    } catch { toast.error("Failed to delete section"); }
  };

  const togglePublish = async () => {
    setSavingPub(true);
    try {
      const newVal = paper.is_published ? 0 : 1;
      await lmsUpdateManualPaper(id, { is_published: newVal });
      toast.success(newVal ? "Paper published" : "Paper set to draft");
      loadPaper();
    } catch { toast.error("Failed to update"); }
    finally { setSavingPub(false); }
  };

  if (loading) return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "60vh" }}>
      <Loader size={28} className="ms2-spin" color="#4f46e5" />
    </div>
  );

  if (!paper) return (
    <div style={{ textAlign: "center", padding: 60, color: "#9ca3af" }}>Paper not found.</div>
  );

  const totalQ = paper.sections?.reduce((s, sec) => s + (sec.questions?.length || 0), 0) || 0;
  const totalMarks = paper.sections?.reduce((s, sec) =>
    s + (sec.questions?.reduce((sq, q) => sq + parseFloat(q.marks || sec.marks_per_q || 0), 0) || 0), 0) || 0;
  const nextSectionLabel = SECTION_LABELS[paper.sections?.length || 0] || "A";

  return (
    <div className="lms-content-inner">
      {/* Header card */}
      <div style={{
        background: "#fff", borderRadius: 14, marginBottom: 20,
        border: "1px solid #e5e7eb", boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
        overflow: "hidden",
      }}>
        {/* Top gradient bar */}
        <div style={{ height: 4, background: "linear-gradient(90deg,#4f46e5,#7c3aed,#0891b2)" }} />

        <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 14 }}>
          <button onClick={() => navigate("/lms/exams")} style={{
            display: "flex", alignItems: "center", gap: 5, fontSize: 13, fontWeight: 600,
            color: "#6b7280", background: "#f3f4f6", border: "none", borderRadius: 8,
            padding: "7px 12px", cursor: "pointer", flexShrink: 0,
          }}>
            <ArrowLeft size={14} /> Back
          </button>

          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 800, fontSize: 17, color: "#111827", marginBottom: 4 }}>
              {paper.title}
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {[
                paper.class_name && `Class: ${paper.class_name}`,
                paper.subject_name && `Subject: ${paper.subject_name}`,
                paper.duration_minutes && `${paper.duration_minutes} min`,
                paper.paper_set && `Set ${paper.paper_set}`,
              ].filter(Boolean).map((item, i) => (
                <span key={i} style={{
                  fontSize: 11, fontWeight: 600, color: "#6b7280",
                  background: "#f3f4f6", borderRadius: 5, padding: "2px 8px",
                }}>{item}</span>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div style={{ display: "flex", gap: 16, flexShrink: 0 }}>
            {[
              { label: "Sections", value: paper.sections?.length || 0, color: "#4f46e5" },
              { label: "Questions", value: totalQ, color: "#0891b2" },
              { label: "Marks", value: totalMarks.toFixed(0), color: "#16a34a" },
            ].map((s) => (
              <div key={s.label} style={{ textAlign: "center" }}>
                <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.value}</div>
                <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 600 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
            <button onClick={() => setShowPreview(true)} style={{
              display: "flex", alignItems: "center", gap: 5, padding: "8px 14px",
              borderRadius: 8, border: "1.5px solid #e5e7eb", background: "#fff",
              color: "#374151", fontSize: 13, fontWeight: 600, cursor: "pointer",
            }}>
              <Eye size={14} /> Preview
            </button>
            <button onClick={togglePublish} disabled={savingPub} style={{
              display: "flex", alignItems: "center", gap: 5, padding: "8px 16px",
              borderRadius: 8, border: "none", fontSize: 13, fontWeight: 700, cursor: "pointer",
              background: paper.is_published
                ? "linear-gradient(135deg,#16a34a,#15803d)"
                : "linear-gradient(135deg,#4f46e5,#7c3aed)",
              color: "#fff",
            }}>
              {savingPub ? <Loader size={13} className="ms2-spin" /> : (
                paper.is_published ? <Check size={14} /> : <BookOpen size={14} />
              )}
              {paper.is_published ? "Published" : "Publish"}
            </button>
          </div>
        </div>
      </div>

      {/* Sections */}
      {paper.sections?.length === 0 && (
        <div style={{ textAlign: "center", padding: "40px 20px", color: "#9ca3af" }}>
          <BookOpen size={40} color="#e5e7eb" style={{ margin: "0 auto 12px" }} />
          <p style={{ fontSize: 15, fontWeight: 600, color: "#374151" }}>No sections yet</p>
          <p style={{ fontSize: 13 }}>Click "+ Add Section" to start building your question paper.</p>
        </div>
      )}

      {paper.sections?.map((sec, si) => (
        <SectionCard
          key={sec.id}
          section={sec}
          index={si}
          onRefresh={loadPaper}
          onDelete={() => setConfirmSec(sec)}
        />
      ))}

      {/* Add section button */}
      <button onClick={() => setAddSection(true)} style={{
        width: "100%", padding: "14px",
        borderRadius: 12, border: "2px dashed #a5b4fc", background: "#f5f3ff",
        color: "#4f46e5", fontSize: 14, fontWeight: 700, cursor: "pointer",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        marginTop: 4, transition: "background 0.15s",
      }}>
        <Plus size={16} /> Add New Section
      </button>

      {/* Modals */}
      {addSection && (
        <AddSectionModal
          nextLabel={nextSectionLabel}
          onClose={() => setAddSection(false)}
          onSave={handleAddSection}
        />
      )}

      {confirmSec && (
        <ConfirmModal
          message={<>Delete <strong>Section {confirmSec.section_label}</strong> and all its questions?</>}
          onConfirm={handleDeleteSection}
          onCancel={() => setConfirmSec(null)}
        />
      )}

      {showPreview && (
        <PrintPreview paper={paper} onClose={() => setShowPreview(false)} />
      )}
    </div>
  );
}
